import { Dispatch } from "react";
import jwtAxios from "../../utils/jwtAxios";
import { AppActions } from "../../types/AppActions";
import { BusinessGroup } from "../../types/models/BusinessGroup";
import { GET_MY_BUSINESS_GROUPS, SELECT_CURRENT_BUSINESS_GROUPS, SELECT_CURRENT_INDOOR } from "../../types/actions/BusinessGroup.actions";
import { fetchError, showMessage } from "./Common";
import { Settings } from "../../types/models/Settings";
import { AxiosResponseWithDetail } from "../../types";
import { Coordinate } from "../../types/models/Mapkit";
import { IndoorExperience } from "../../types/models/IndoorExperience";

type UpdateBeaconGroupPayload = { id: string; name: string; identifier: string; theme: string; title?: string; description?: string; color?: string };

interface NewCategories {
  categoryId: string;
  index: string;
}

export const getMyBusinessGroups = (callBack: (result: { businessGroups?: BusinessGroup[];indoorExperiences?: IndoorExperience[]; }) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("/api/business/myexperiences")
      .then((data) => {
        if (data.status === 200) {
          const businessGroups: BusinessGroup[] = data.data.experiences;
          const indoorExperiences: IndoorExperience[] = data.data.indoor;
          dispatch({ type: GET_MY_BUSINESS_GROUPS, experiencePayload: businessGroups, indoorPayload: indoorExperiences });
          callBack({ businessGroups, indoorExperiences });
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack({});
        }
      })
      .catch((error) => {
        dispatch(fetchError(error.message));
        callBack({});
      });
  };
};

export const getMySettings = (callBack: (settings: Settings[] | undefined) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("/api/business/mysettings")
      .then((data) => {
        if (data.status === 200) {
          const settings: Settings[] = data.data;
          callBack(settings);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(undefined);
        }
      })
      .catch((error) => {
        dispatch(fetchError(error.message));
        callBack(undefined);
      });
  };
};

export const createBeaconGroup = (name: string, identifier: string, callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("/api/business/createbeacongroup", { name: name, identifier: identifier })
      .then((data) => {
        if (data.status === 200) {
          dispatch(showMessage("Created Experience " + name));
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        if (error.response) {
          console.error("Validation Error Details:", error.response.data);
          dispatch(fetchError(error.response.data.detail || "Unknown validation error"));
        } else if (error.request) {
          console.error("No response received:", error.request);
          dispatch(fetchError("No response from server"));
        } else {
          console.error("Error setting up request:", error.message);
          dispatch(fetchError(error.message));
        }
        callBack(false);
      });
  };
};

export const updateExperience = (settings: Settings, callBack: (result: boolean, categoryIds?: NewCategories[]) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios.post('/api/business/updateexperience', settings)
      .then((data: AxiosResponseWithDetail) => {
        if (data.status === 200) {
          const categoryIds = data.data.categories;
          callBack(true, categoryIds);
        } else {
          dispatch(
            fetchError(
              data.detail
                ? data.detail
                : "Request failed. Status:" + data.status
            )
          );
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const createExperience = (settings: Settings, callBack: (result: boolean, id?: string, categoryIds?: NewCategories[]) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios.post('/api/business/createexperience', settings)
      .then((data: AxiosResponseWithDetail) => {
        if (data.status === 200) {
          const categoryIds = data.data.categories;
          const groupId = data.data.groupId;
          callBack(true, groupId, categoryIds);
        } else {
          dispatch(
            fetchError(
              data.detail
                ? data.detail
                : "Request failed. Status:" + data.status
            )
          );
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const checkIdentifier = (identifier: string, callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios.get('/api/business/checkidentifier?identifier=' + identifier)
      .then((data: AxiosResponseWithDetail) => {
        if (data.status === 200) {
          const check = data.data;
          callBack(check);
        } else {
          dispatch(
            fetchError(
              data.detail
                ? data.detail
                : "Request failed. Status:" + data.status
            )
          );
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const updateBeaconGroup = (groupId: string, name: string, subdomain: string, theme: string, callBack: (result: boolean) => void) => {
  const payload: UpdateBeaconGroupPayload = { id: groupId, name: name, identifier: subdomain, theme: theme };

  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("/api/business/updatebeacongroup", payload)
      .then((data) => {
        if (data.status === 200) {
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        console.log("error", error);
        if (error.response && error.response.data && error.response.data.detail) {
          dispatch(fetchError(error.response.data.detail));
        } else {
          dispatch(fetchError(error.message));
        }
        callBack(false);
      });
  };
};

export const updateBeaconGroupSettings = (
  groupId: string,
  name: string,
  subdomain: string,
  theme: string,
  title: string,
  description: string,
  color: string,
  callBack: (result: boolean) => void
) => {
  const payload: UpdateBeaconGroupPayload = {
    id: groupId,
    name: name,
    identifier: subdomain,
    theme: theme,
    title: title,
    description: description,
    color: color.replace("#", ""),
  };

  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("/api/business/updatebeacongroup", payload)
      .then((data) => {
        console.log("data", data);
        if (data.status === 200) {
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        console.log("error", error);
        if (error.response && error.response.data && error.response.data.detail) {
          dispatch(fetchError(error.response.data.detail));
        } else {
          dispatch(fetchError(error.message));
        }
        callBack(false);
      });
  };
};

export const updateIndoorExperience = (groupId: string, name: string, identifier: string | null, callBack: (result: boolean) => void) => {
  const payload = { id: groupId, name: name, identifier: identifier};

  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("/api/business/editindoorexperience", payload)
      .then((data) => {
        if (data.status === 200) {
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        if (error.response && error.response.data && error.response.data.detail) {
          dispatch(fetchError(error.response.data.detail));
        } else {
          dispatch(fetchError(error.message));
        }
        callBack(false);
      });
  };
};

export const uploadSettingsAsset = (group_id: string, formData: FormData, callback: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {

    const config = {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    };

    const url = "/api/business/uploadsettingsasset?beacongroupid=" + group_id;

    jwtAxios
      .post(url, formData, config)
      .then((data) => {
        if (data.status === 200) {
          callback(true);
        } else {
          dispatch(fetchError("Request failed. Status: " + data.status));
          callback(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callback(false);
      });
  };
};

export const deleteSettingsAsset = (group_id: string, callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios.post('/api/business/deletesettingsasset?beacongroupid=' + group_id)
      .then((data) => {
        if (data.status === 200) {
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status: " + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const uploadQRIcon = (group_id: string, formData: FormData, callback: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {

    const config = {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    };

    const url = "/api/business/uploadqricon?beacongroupid=" + group_id;

    jwtAxios
      .post(url, formData, config)
      .then((data) => {
        if (data.status === 200) {
          callback(true);
        } else {
          dispatch(fetchError("Request failed. Status: " + data.status));
          callback(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callback(false);
      });
  };
};

export const deleteQRIcon = (group_id: string, callback: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {

    const url = "/api/business/deleteqricon?beacongroupid=" + group_id;

    jwtAxios
      .post(url)
      .then((data) => {
        if (data.status === 200) {
          callback(true);
        } else {
          dispatch(fetchError("Request failed. Status: " + data.status));
          callback(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callback(false);
      });
  };
};

export const setCurrentBusinessGroup = (businessGroup: BusinessGroup | null) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch({ type: SELECT_CURRENT_BUSINESS_GROUPS, payload: businessGroup });
  };
};

export const setCurrentIndoorExperience = (businessGroup: IndoorExperience | null) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch({ type: SELECT_CURRENT_INDOOR, payload: businessGroup });
  };
};

export const deleteBusinessGroup = (groupId: string, callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("/api/business/deleteexperience", { id: groupId })
      .then((data) => {
        if (data.status === 200) {
          dispatch(showMessage("The Experience has been deleted"));
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const clearBeaconGroups = () => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch({ type: GET_MY_BUSINESS_GROUPS, experiencePayload: [], indoorPayload: [] });
    dispatch({ type: SELECT_CURRENT_BUSINESS_GROUPS, payload: null });
    dispatch({ type: SELECT_CURRENT_INDOOR, payload: null });
  };
};

export const archiveBeaconGroup = (group_id: string, archived: boolean, callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post('/api/business/archivegroup?group_id=' + group_id)
      .then((data) => {
        if (data.status === 200) {
          dispatch(showMessage("The Experience has been " + (archived ? "unarchived" : "archived")));
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const duplicateBeaconGroup = (group_id: string, name: string, callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post('/api/business/duplicateexperience?group_id=' + group_id + "&name=" + name)
      .then((data) => {
        if (data.status === 200) {
          dispatch(showMessage("The Experience has been duplicated"));
          callBack(true);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const shareBeaconGroup = (group_id: string, email: string, accessLevel: string, callBack: (result: boolean, code?: string) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post('/api/business/creategroupinvite?group_id=' + group_id + "&email=" + email + "&accessLevel=" + accessLevel)
      .then((data) => {
        if (data.status === 200) {
          dispatch(showMessage("A share invite link has been created"));
          callBack(true, data.data.invitecode);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const acceptBeaconGroup = (code: string, callBack: (result: boolean, beacongroupid?: string) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post('/api/business/acceptgroupinvite?code=' + code)
      .then((data) => {
        if (data.status === 200) {
          dispatch(showMessage("Accepted Experience invite"));
          callBack(true, data.data.beacongroupid);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack(false);
      });
  };
};

export const getWebARAnalytics = (group_id: string, callBack: (analytics: []) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("/api/business/webaranalytics?group_id=" + group_id)
      .then((data) => {
        if (data.status === 200) {
          callBack(data.data);
        } else {
          dispatch(fetchError("Request failed. Status:" + data.status));
          callBack([]);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response.data.detail
            ? error.response.data.detail
            : error.message));
        callBack([]);
      });
  };
};

export const getAnalytics = (group_id: string, date_range: string, callback: (data: any) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get(`/api/business/analytics?group_id=${group_id}&date_range=${date_range}`)
      .then((response) => {
        if (response.status === 200) {
          callback(response.data);
        } else {
          dispatch(fetchError("Request failed. Status:" + response.status));
          callback([]);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response?.data?.detail || error.message
        ));
        callback([]);
      });
  };
};

export const createRealEstateListing = (address: string, coordinates: Coordinate, listingURL: string, callback: (data: any) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("/api/business/createrealestatelisting", {
        address,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        url: listingURL,
      })
      .then((response) => {
        if (response.status === 200) {
          callback(response.data.jobId);
        } else {
          dispatch(fetchError("Request failed. Status:" + response.status));
          callback(null);
        }
      })
      .catch((error) => {
        dispatch(fetchError(
          error.response?.data?.detail || error.message
        ));
        callback(null);
      });
  };
};